/**
 * The one Stripe-connect surface (ADR 0044). ONE-CLICK: opening the dialog
 * immediately mints an account session (the server creates the Express
 * account on first call, seeded with the signed-in admin's email) and renders
 * EMBEDDED Stripe onboarding inside the dialog — no details form, the org
 * never leaves dash.staty.io. Stripe's own flow collects business type and
 * confirms the email. Falls back to the Stripe-hosted account link if the
 * embedded session can't be minted.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth, useUser } from '@/auth/clerkShim';
import useOrganizationStore from '@store/organizationStore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import StripeEmbeddedOnboarding, { AccountSession } from './StripeEmbeddedOnboarding';

interface StripeSetupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Organization to connect. */
  orgId: string | undefined;
  /** Called when the user exits onboarding (status may have changed). */
  onComplete?: () => void;
  trigger?: React.ReactNode;
}

export default function StripeSetupModal({
  open,
  onOpenChange,
  orgId,
  onComplete,
  trigger,
}: StripeSetupModalProps) {
  const { user } = useUser();
  const { getToken } = useAuth();
  // Select the curried factories and bind getToken OUTSIDE the selector: a
  // selector that calls the factory returns a fresh function every run, which
  // useSyncExternalStore treats as a changed snapshot and forces re-renders —
  // the forced re-render during the onboarding mount is what double-created
  // ConnectInstances (see StripeEmbeddedOnboarding).
  const sessionFactory = useOrganizationStore((s: any) => s.createStripeAccountSession);
  const linkFactory = useOrganizationStore((s: any) => s.createStripeConnectLink);
  const createStripeAccountSession = useMemo(
    () => sessionFactory(getToken), [sessionFactory, getToken]
  );
  const createStripeConnectLink = useMemo(
    () => linkFactory(getToken), [linkFactory, getToken]
  );

  const [session, setSession] = useState<AccountSession | null>(null);
  const [starting, setStarting] = useState(false);
  const [sessionFailed, setSessionFailed] = useState(false);

  // One-click: opening the dialog goes straight to embedded onboarding. The
  // server defaults the account email from the signed-in admin.
  useEffect(() => {
    if (open && orgId && !session && !starting && !sessionFailed) {
      setStarting(true);
      void createStripeAccountSession(orgId, {}).then((result: AccountSession | null) => {
        setStarting(false);
        if (result?.clientSecret && result?.publishableKey) {
          setSession(result);
        } else {
          setSessionFailed(true);
        }
      });
    }
    if (!open) {
      // Fresh session (single-use client secrets) on every open.
      setSession(null);
      setSessionFailed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orgId]);

  const handleExit = () => {
    onComplete?.();
    onOpenChange(false);
  };

  // Stripe-hosted fallback — same account, new tab (the pre-ADR-0044 flow).
  const openHostedFallback = () => {
    const adminId = (user as { id?: string } | null)?.id;
    void createStripeConnectLink(
      adminId,
      user?.primaryEmailAddress?.emailAddress,
      'individual',
      (data: { accountLink?: string }) => {
        if (data?.accountLink) window.open(data.accountLink, '_blank');
      },
      orgId,
      window.location.href
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className={session ? "max-w-2xl max-h-[85vh] overflow-y-auto" : undefined}>
        <DialogHeader>
          <DialogTitle>Set up payments</DialogTitle>
          <DialogDescription>
            Choose where registration revenue should be deposited and verify your
            organization. Payouts go directly to your bank account.
          </DialogDescription>
        </DialogHeader>

        {session ? (
          <StripeEmbeddedOnboarding
            session={session}
            refreshClientSecret={async () => {
              const next = await createStripeAccountSession(orgId, {});
              return next?.clientSecret ?? null;
            }}
            onExit={handleExit}
          />
        ) : (
          <div className="space-y-4 py-4" data-testid="stripe-setup-pending">
            {!sessionFailed && (
              <p className="text-sm text-muted-foreground">Loading secure setup…</p>
            )}
            {sessionFailed && (
              <div className="space-y-2" data-testid="stripe-hosted-fallback">
                <p className="text-sm text-muted-foreground">
                  In-page setup is unavailable right now.
                </p>
                <Button variant="outline" className="w-full" onClick={openHostedFallback}>
                  Continue in a new tab
                </Button>
              </div>
            )}
          </div>
        )}
        <p className="pt-2 text-center text-xs text-muted-foreground">
          Payments securely processed by Stripe.
        </p>
      </DialogContent>
    </Dialog>
  );
}
